import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { addMinutes, isBefore } from "date-fns";

/** Common US timezones for the dropdown */
export const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HT)" },
  { value: "America/Phoenix", label: "Arizona (MST)" },
] as const;

/** Get the browser's IANA timezone */
export const getBrowserTimezone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Convert a date string + time string (e.g. "2024-04-16" + "09:00")
 * in a given source timezone into a UTC Date.
 */
export const dateTimeInTzToUtc = (
  dateStr: string,
  timeStr: string,
  timezone: string,
): Date => {
  // fromZonedTime: given a "wall clock" datetime in a timezone, returns the equivalent UTC Date
  return fromZonedTime(`${dateStr}T${timeStr}:00`, timezone);
};

/**
 * Format an ISO/UTC date string for display in a given timezone.
 * Uses date-fns format tokens.
 */
export const formatInTz = (
  utcDate: string | Date,
  timezone: string,
  formatStr: string,
): string => {
  const d = typeof utcDate === "string" ? new Date(utcDate) : utcDate;
  return formatInTimeZone(d, timezone, formatStr);
};

/**
 * Get the short timezone abbreviation for display, e.g. "EST", "PST".
 */
export const getTimezoneAbbr = (timezone: string, date?: Date): string => {
  const d = date ?? new Date();
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(d);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? timezone;
  } catch {
    return timezone;
  }
};

/**
 * Generate bookable time slots for a given date by interpreting provider
 * availability windows in the provider's timezone, then returning UTC Date objects.
 */
export const generateSlotsForDay = (
  dateStr: string,
  providerTz: string,
  availWindows: { start_time: string; end_time: string }[],
  slotDuration: number,
  existingUtcStarts: Set<number>,
): Date[] => {
  const now = new Date();
  const slots: Date[] = [];

  for (const win of availWindows) {
    const winStart = dateTimeInTzToUtc(dateStr, win.start_time.slice(0, 5), providerTz);
    const winEnd = dateTimeInTzToUtc(dateStr, win.end_time.slice(0, 5), providerTz);

    let current = new Date(winStart);
    while (addMinutes(current, slotDuration) <= winEnd) {
      if (!isBefore(current, now) && !existingUtcStarts.has(current.getTime())) {
        slots.push(new Date(current));
      }
      current = addMinutes(current, slotDuration);
    }
  }

  return slots;
};
